import autoTable from 'jspdf-autotable';

// Diseño institucional compartido por todos los certificados CALINS.
// La plantilla maestra es el certificado de Presión. Los generadores de cada
// magnitud conservan únicamente sus tablas y contenido metrológico específico.

export const CERTIFICATE_LOGO_URL = '/logo-calins-color-hd.png';
export const CERTIFICATE_TABLE_HEAD_COLOR = [180, 215, 225];
export const CERTIFICATE_TABLE_HEAD_TEXT_COLOR = [0, 0, 0];
export const CERTIFICATE_SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

export const CERTIFICATE_LEGAL_PARAGRAPHS = [
  'Este certificado se expide de acuerdo a lo establecido en la Norma ISO/IEC 17025.',
  'Este certificado de calibración/medición documenta la trazabilidad a patrones que representan las unidades físicas de medida en concordancia con el Sistema Internacional de Unidades (SI).',
  'Este certificado no podrá reproducirse parcialmente sin permiso escrito del laboratorio. Los certificados sin firma y aclaración no serán válidos.',
  'El usuario es responsable de establecer intervalos apropiados de recalibración.',
];

export function certificateDate(value, fallback = '-') {
  if (!value) return fallback;
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return fallback;
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

export function resolveCertificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '-';
}

export async function certificateImageAsDataUrl(url) {
  try {
    const source = String(url || '');
    if (source.startsWith('data:')) {
      const separator = source.indexOf(',');
      return separator >= 0 && source.slice(separator + 1).trim() ? source : null;
    }
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export function drawCertificateHeader(doc, {
  logo,
  number,
  page,
  pages,
  first = false,
  simulation = false,
}) {
  const width = doc.internal.pageSize.getWidth();
  const title = `CERTIFICADO DE CALIBRACIÓN / MEDICIÓN N° ${number || '-'}`;

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(first ? 12 : 11);
  doc.text(title, width / 2, 18, { align: 'center' });

  if (simulation) {
    doc.setTextColor(185, 28, 28);
    doc.setFontSize(9.5);
    doc.text('SIMULACIÓN - NO VÁLIDO', width / 2, 25, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Pag. ${page} de ${pages}`, width - 14, 28, { align: 'right' });

  if (first) {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(14, 30, width - 14, 30);
    doc.line(14, 58, width - 14, 58);
    if (logo) doc.addImage(logo, 'PNG', 16, 35, 60, 18, undefined, 'FAST');

    const address = [
      'Rodríguez Peña 2065',
      'Gral. Gutierrez - Maipú',
      'Mendoza - ARGENTINA',
      'Cel: +54-9-261-5322685',
      'laboratorio@calins.com.ar',
    ];
    doc.setFontSize(11);
    address.forEach((line, index) => {
      doc.setTextColor(0, 0, index === address.length - 1 ? 255 : 0);
      doc.text(line, width - 14, 38 + (index * 4.8), { align: 'right' });
    });
    doc.setTextColor(0, 0, 0);
  } else if (logo) {
    doc.addImage(logo, 'PNG', 14, 10, 26, 7.8, undefined, 'FAST');
  }
}

export function drawCertificateFooter(doc, page, pages) {
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110, 110, 110);
  doc.text(`Pag. ${page} de ${pages}`, 14, height - 8);
  doc.text('CALINS Laboratorio', width - 14, height - 8, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

export function drawCertificateLegalText(doc, y = 66, { x = 15, width = 180, fontSize = 9.5 } = {}) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);
  let cursor = y;
  CERTIFICATE_LEGAL_PARAGRAPHS.forEach((paragraph) => {
    const lines = doc.splitTextToSize(paragraph, width);
    doc.text(lines, x, cursor);
    cursor += lines.length * 4.4 + 3;
  });
  return cursor;
}

export function certificateHeadStyles(overrides = {}) {
  return {
    fillColor: CERTIFICATE_TABLE_HEAD_COLOR,
    textColor: CERTIFICATE_TABLE_HEAD_TEXT_COLOR,
    fontStyle: 'bold',
    ...overrides,
  };
}

function referenceText(value, fallback = '-') {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function referenceDescription(reference = {}) {
  if (reference.description) return referenceText(reference.description);
  const asset = reference.asset || {};
  const identity = [asset.code, asset.brand_model || asset.name].filter(Boolean).join(' · ');
  return referenceText(identity);
}

function referenceScope(reference = {}) {
  if (reference.scope) return referenceText(reference.scope);
  const capability = reference.capability || {};
  const minimum = capability.range_min;
  const maximum = capability.range_max;
  const unit = referenceText(capability.unit, '');
  if (minimum == null && maximum == null) return '-';
  if (minimum != null && maximum != null) return `${minimum} a ${maximum}${unit ? ` ${unit}` : ''}`;
  return `${minimum ?? maximum}${unit ? ` ${unit}` : ''}`;
}

/**
 * Sección institucional única para identificar los patrones utilizados.
 * Admite uno o varios instrumentos/materiales sin cambiar el diseño del PDF.
 */
export function drawReferenceStandardsTable(doc, {
  startY,
  references = [],
  title = 'PATRONES DE REFERENCIA',
  margin = { left: 14, right: 14 },
  fontSize = 6.8,
} = {}) {
  const normalized = (references || []).filter(Boolean);
  const rows = (normalized.length ? normalized : [{}]).map((reference) => {
    const asset = reference.asset || {};
    const certificate = reference.certificate || {};
    return [
      referenceText(reference.usage || reference.usageLabel || 'Referencia'),
      referenceDescription(reference),
      referenceText(reference.serial ?? asset.serial),
      referenceText(reference.certificateNo ?? certificate.certificate_no),
      certificateDate(reference.calibrationDate ?? certificate.calibration_date),
      certificateDate(reference.validUntil ?? certificate.valid_until),
      referenceScope(reference),
    ];
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, margin.left, startY);
  autoTable(doc, {
    startY: startY + 5,
    margin,
    theme: 'grid',
    styles: { fontSize, cellPadding: 1.35, lineWidth: 0.1, valign: 'middle' },
    headStyles: certificateHeadStyles(),
    head: [['Uso', 'Código / patrón', 'Serie / lote', 'Certificado', 'Calibración', 'Vigencia', 'Alcance']],
    body: rows,
    columnStyles: {
      0: { cellWidth: 19 },
      1: { cellWidth: 48 },
      2: { cellWidth: 24 },
      3: { cellWidth: 27 },
      4: { cellWidth: 20 },
      5: { cellWidth: 20 },
      6: { cellWidth: 'auto' },
    },
  });
  return doc.lastAutoTable?.finalY || (startY + 14);
}
